import { StringDevUIFieldDescriptor as UIFieldDescriptor } from "../../core/ui/StringDevUISchema";
import { GridAdapter } from "./GridAdapter";

/** Golden ratio constant */
const PHI = 1.6180339887;

/**
 * Phi Grid adapter.
 * Places lines at golden ratio divisions (1/φ ≈ 0.618 and 1 - 1/φ ≈ 0.382).
 * More harmonious than Rule of Thirds — used in classical composition.
 */
export class PhiGridAdapter extends GridAdapter {
  readonly type = "phi";
  readonly label = "Phi";
  readonly icon = `<svg viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg"><line x1="6.1" y1="1" x2="6.1" y2="15" stroke="currentColor" stroke-opacity="0.5"/><line x1="9.9" y1="1" x2="9.9" y2="15" stroke="currentColor" stroke-opacity="0.5"/><line x1="1" y1="6.1" x2="15" y2="6.1" stroke="currentColor" stroke-opacity="0.5"/><line x1="1" y1="9.9" x2="15" y2="9.9" stroke="currentColor" stroke-opacity="0.5"/><text x="8" y="8.5" text-anchor="middle" font-size="4" fill="currentColor" opacity="0.3">φ</text></svg>`;

  getDefaults(): Record<string, any> {
    return {
      color: "#c24ae2",
      opacity: 0.4,
      showIntersections: true,
    };
  }

  getUISchema(): UIFieldDescriptor[] {
    return [
      { type: "color", key: "color", label: "Color", default: "#c24ae2" },
      { type: "range", key: "opacity", label: "Opacity", default: 0.4, min: 0, max: 1, step: 0.01 },
      { type: "toggle", key: "showIntersections", label: "Show points", default: true },
    ];
  }

  render(
    svg: SVGSVGElement,
    width: number,
    height: number,
    settings: Record<string, any>,
  ): void {
    const group = this.getGroup(svg, settings.__instanceId);
    const { color, opacity, showIntersections } = settings;

    const ratioSmall = 1 / PHI;       // ≈ 0.618
    const ratioLarge = 1 - ratioSmall; // ≈ 0.382

    const vx1 = width * ratioLarge;
    const vx2 = width * ratioSmall;
    const hy1 = height * ratioLarge;
    const hy2 = height * ratioSmall;

    // Vertical lines
    group.appendChild(this.createLine(vx1, 0, vx1, height, color, opacity));
    group.appendChild(this.createLine(vx2, 0, vx2, height, color, opacity));

    // Horizontal lines
    group.appendChild(this.createLine(0, hy1, width, hy1, color, opacity));
    group.appendChild(this.createLine(0, hy2, width, hy2, color, opacity));

    if (showIntersections) {
      const points = [
        [vx1, hy1], [vx1, hy2],
        [vx2, hy1], [vx2, hy2],
      ];

      for (const [cx, cy] of points) {
        const circle = document.createElementNS("http://www.w3.org/2000/svg", "circle");
        circle.setAttribute("cx", String(cx));
        circle.setAttribute("cy", String(cy));
        circle.setAttribute("r", "3");
        circle.setAttribute("fill", color);
        circle.setAttribute("fill-opacity", String(opacity * 1.5));
        group.appendChild(circle);
      }
    }
  }
}
